/**
 * Mini App fetch client（ADR-0004 §4.2 / §4.4 + ADR-0006 envelope）。
 *
 * - 基地址来自 VITE_API_BASE_URL（构建期注入；客户端可见 → **不得**含任何 secret）
 * - Authorization: Bearer <session-jwt>（从 memory-only auth store 读）
 * - 响应统一是 api-server 的 envelope `{ success, data, error, traceId }`；这里直接返回 data 或 throw ApiError
 */

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

export interface ApiError extends Error {
  status: number;
  code: string;
  traceId?: string;
  details?: unknown;
}

function makeApiError(
  status: number,
  code: string,
  message: string,
  traceId?: string,
  details?: unknown,
): ApiError {
  const err = new Error(message) as ApiError;
  err.name = 'ApiError';
  err.status = status;
  err.code = code;
  err.traceId = traceId;
  err.details = details;
  return err;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  traceId?: string;
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  /** Extra request headers */
  headers?: Record<string, string>;
  /** Override AbortSignal for cancellation */
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  if (!API_BASE_URL) {
    throw makeApiError(0, 'CLIENT_MISCONFIG', 'VITE_API_BASE_URL not configured');
  }
  const { method = 'GET', body, token, headers = {}, signal } = options;
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = { Accept: 'application/json', ...headers };
  if (body !== undefined) finalHeaders['Content-Type'] = 'application/json';
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    throw makeApiError(0, 'NETWORK_ERROR', (err as Error).message || 'Network failure');
  }

  let payload: Envelope<T> | null = null;
  try {
    payload = (await res.json()) as Envelope<T>;
  } catch {
    throw makeApiError(res.status, 'MALFORMED_RESPONSE', `Non-JSON response (HTTP ${res.status})`);
  }

  if (!res.ok || !payload || payload.success !== true) {
    const errEnv = payload?.error;
    throw makeApiError(
      res.status,
      errEnv?.code ?? 'UNKNOWN_ERROR',
      errEnv?.message ?? `HTTP ${res.status}`,
      payload?.traceId,
      errEnv?.details,
    );
  }
  // Envelope contract: success=true ⇒ data present
  return payload.data as T;
}

/* -----------------------------------------------------------------------
 * Consumer API typed helpers（SPEC-002 §3 Groupon / Order / Payment）
 *
 * BigInt 金额字段在 JSON 序列化时变成 string；这里保留 string 原样，
 * 展示层调用 `formatPrice` 转换。
 * --------------------------------------------------------------------- */

export interface ConsumerCity {
  id: string;
  code: string;
  nameZh: string;
  nameKm: string;
  nameEn: string;
  timezone: string;
}

export interface ConsumerCategory {
  id: string;
  code: string;
  nameZh: string;
  nameKm: string;
  nameEn: string;
  iconUrl: string | null;
  comingSoon: boolean;
  orderIndex: number;
}

export type ContentConfigPlacement =
  | 'HOME_CAROUSEL'
  | 'GROUPON_PROMO_BANNER'
  | 'HOME_ACTIVITY';

export interface ConsumerContentConfig {
  id: string;
  placement: ContentConfigPlacement;
  cityCode: string | null;
  titleZh: string;
  titleKm: string;
  titleEn: string;
  subtitleZh: string | null;
  subtitleKm: string | null;
  subtitleEn: string | null;
  imageUrl: string;
  targetUrl: string;
  orderIndex: number;
}

export function getCities(signal?: AbortSignal): Promise<{ items: ConsumerCity[] }> {
  return apiFetch<{ items: ConsumerCity[] }>('/api/v1/consumer/cities', { signal });
}

export function getCategories(signal?: AbortSignal): Promise<{ items: ConsumerCategory[] }> {
  return apiFetch<{ items: ConsumerCategory[] }>('/api/v1/consumer/categories', { signal });
}

export function getContentConfigs(
  params: { placement: ContentConfigPlacement; cityCode?: string },
  signal?: AbortSignal,
): Promise<{ items: ConsumerContentConfig[] }> {
  const qs = new URLSearchParams({ placement: params.placement });
  if (params.cityCode) qs.set('cityCode', params.cityCode);
  return apiFetch<{ items: ConsumerContentConfig[] }>(
    `/api/v1/consumer/content-configs?${qs.toString()}`,
    { signal },
  );
}

/* ----- P0-D-02: 商家搜索 / 详情 / 自有资料 ----- */

export interface ConsumerMerchantSummary {
  id: string;
  nameZh: string;
  nameKm: string | null;
  nameEn: string | null;
  cityCode: string;
  categoryCode: string;
  coverImageUrl: string | null;
  address: string | null;
  lowestPriceCents: string | null;
  lowestPriceCurrency: string | null;
  activeDealCount: number;
}

export interface ConsumerMerchantDetail {
  id: string;
  nameZh: string;
  nameKm: string | null;
  nameEn: string | null;
  descriptionI18n: { zh?: string; km?: string; en?: string } | null;
  cityCode: string;
  cityNameZh: string;
  categoryCode: string;
  categoryNameZh: string;
  address: string | null;
  phone: string | null;
  phoneDisplayMode: string;
  businessHours: unknown;
  coverImageUrl: string | null;
  activeDeals: Array<{
    id: string;
    titleZh: string;
    titleKm: string | null;
    titleEn: string | null;
    priceCents: string;
    originalPriceCents: string;
    currency: string;
    coverImageUrl: string | null;
    soldCount: number;
    saleEndAt: string;
  }>;
}

export interface SearchMerchantsQuery {
  q?: string;
  cityCode?: string;
  categoryCode?: string;
  page?: number;
  pageSize?: number;
}

export function searchMerchants(
  query: SearchMerchantsQuery,
  signal?: AbortSignal,
): Promise<{
  items: ConsumerMerchantSummary[];
  page: number;
  pageSize: number;
  total: number;
}> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{
    items: ConsumerMerchantSummary[];
    page: number;
    pageSize: number;
    total: number;
  }>(`/api/v1/consumer/merchants${suffix}`, { signal });
}

/** SPEC-012 — 统一搜索（SR-09） */
export interface ConsumerSearchResultItem {
  type: 'merchant' | 'groupon' | 'discover' | 'review';
  id: string;
  score: number;
  summary: {
    title: string;
    snippet: string;
    thumbUrl: string | null;
    cityCode: string | null;
    priceCents: string | null;
  };
}

export interface ConsumerSearchResponse {
  query: string;
  total: number;
  page: number;
  pageSize: number;
  results: ConsumerSearchResultItem[];
}

export function consumerSearch(
  q: string,
  opts: { cityCode?: string; type?: 'merchant' | 'groupon' | 'discover' | 'review' | 'all'; page?: number; pageSize?: number },
  signal?: AbortSignal,
): Promise<ConsumerSearchResponse> {
  const qs = new URLSearchParams({ q });
  if (opts.cityCode) qs.set('cityCode', opts.cityCode);
  if (opts.type) qs.set('type', opts.type);
  if (opts.page) qs.set('page', String(opts.page));
  if (opts.pageSize) qs.set('pageSize', String(opts.pageSize));
  return apiFetch<ConsumerSearchResponse>(`/api/v1/consumer/search?${qs.toString()}`, { signal });
}

export interface PublicHotKeywordItem {
  queryText: string;
  cityCode: string | null;
  displayRank: number;
}

export function getPublicSearchHot(
  cityCode: string | undefined,
  signal?: AbortSignal,
): Promise<{ items: PublicHotKeywordItem[] }> {
  const qs = cityCode ? `?cityCode=${encodeURIComponent(cityCode)}` : '';
  return apiFetch<{ items: PublicHotKeywordItem[] }>(`/api/v1/consumer/search/hot${qs}`, {
    signal,
  });
}

export function getMerchantById(
  id: string,
  signal?: AbortSignal,
): Promise<{ merchant: ConsumerMerchantDetail }> {
  return apiFetch<{ merchant: ConsumerMerchantDetail }>(
    `/api/v1/consumer/merchants/${encodeURIComponent(id)}`,
    { signal },
  );
}

export interface ConsumerMeProfile {
  id: string;
  telegramId: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  languageCode: string | null;
  preferredLocale: 'zh-CN' | 'km-KH' | 'en' | null;
  photoUrl: string | null;
  lastLoginAt: string | null;
}

export function getMeProfile(
  token: string,
  signal?: AbortSignal,
): Promise<{ user: ConsumerMeProfile }> {
  return apiFetch<{ user: ConsumerMeProfile }>('/api/v1/consumer/me', {
    token,
    signal,
  });
}

export function updateMyPreferences(
  payload: { preferredLocale: 'zh-CN' | 'km-KH' | 'en' | null },
  token: string,
  signal?: AbortSignal,
): Promise<{ user: ConsumerMeProfile }> {
  return apiFetch<{ user: ConsumerMeProfile }>('/api/v1/consumer/me/preferences', {
    method: 'PATCH',
    body: payload,
    token,
    signal,
  });
}

/* ----- P0-D-03: 探店 + 点评 ----- */

export interface PublicDiscoverPost {
  id: string;
  merchantId: string;
  merchantNameZh: string;
  cityCode: string;
  titleZh: string;
  titleKm: string | null;
  titleEn: string | null;
  contentI18n: { zh?: string; km?: string; en?: string };
  coverImageUrl: string | null;
  likeCount: number;
  publishedAt: string | null;
  createdAt: string;
}

export interface DiscoverPostDetail {
  id: string;
  merchantId: string;
  merchantNameZh: string;
  cityCode: string;
  titleZh: string;
  titleKm: string | null;
  titleEn: string | null;
  contentI18n: { zh?: string; km?: string; en?: string };
  coverImageUrl: string | null;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  likedByMe: boolean;
  publishedAt: string | null;
  createdAt: string;
}

export interface DiscoverComment {
  id: string;
  telegramUserId: string;
  content: string;
  createdAt: string;
}

export function getDiscoverPostById(
  id: string,
  token?: string | null,
  signal?: AbortSignal,
): Promise<{ post: DiscoverPostDetail }> {
  return apiFetch<{ post: DiscoverPostDetail }>(
    `/api/v1/consumer/discover-posts/${encodeURIComponent(id)}`,
    { token: token ?? undefined, signal },
  );
}

export function toggleDiscoverPostLike(
  postId: string,
  token: string,
  signal?: AbortSignal,
): Promise<{ liked: boolean; likeCount: number }> {
  return apiFetch<{ liked: boolean; likeCount: number }>(
    `/api/v1/consumer/discover-posts/${encodeURIComponent(postId)}/like`,
    { method: 'POST', token, signal },
  );
}

export function getDiscoverPostComments(
  postId: string,
  query: { page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<{
  items: DiscoverComment[];
  page: number;
  pageSize: number;
  total: number;
}> {
  const qs = new URLSearchParams();
  if (query.page) qs.set('page', String(query.page));
  if (query.pageSize) qs.set('pageSize', String(query.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{
    items: DiscoverComment[];
    page: number;
    pageSize: number;
    total: number;
  }>(`/api/v1/consumer/discover-posts/${encodeURIComponent(postId)}/comments${suffix}`, {
    signal,
  });
}

export function createDiscoverPostComment(
  postId: string,
  content: string,
  token: string,
  signal?: AbortSignal,
): Promise<{ comment: DiscoverComment }> {
  return apiFetch<{ comment: DiscoverComment }>(
    `/api/v1/consumer/discover-posts/${encodeURIComponent(postId)}/comments`,
    { method: 'POST', body: { content }, token, signal },
  );
}

export function shareDiscoverPost(
  postId: string,
  token: string,
  platform: string = 'telegram',
  signal?: AbortSignal,
): Promise<{ shareCount: number }> {
  return apiFetch<{ shareCount: number }>(
    `/api/v1/consumer/discover-posts/${encodeURIComponent(postId)}/share`,
    { method: 'POST', body: { platform }, token, signal },
  );
}

export function addFavorite(
  targetType: 'MERCHANT' | 'DISCOVER_POST',
  targetId: string,
  token: string,
  signal?: AbortSignal,
): Promise<{ favorite: { id: string; targetType: string; targetId: string; createdAt: string } }> {
  return apiFetch<{ favorite: { id: string; targetType: string; targetId: string; createdAt: string } }>(
    '/api/v1/consumer/me/favorites',
    { method: 'POST', body: { targetType, targetId }, token, signal },
  );
}

export function removeFavorite(
  favoriteId: string,
  token: string,
  signal?: AbortSignal,
): Promise<void> {
  return apiFetch<void>(
    `/api/v1/consumer/me/favorites/${encodeURIComponent(favoriteId)}`,
    { method: 'DELETE', token, signal },
  );
}

export function getDiscoverPosts(
  query: { cityCode?: string; page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<{
  items: PublicDiscoverPost[];
  page: number;
  pageSize: number;
  total: number;
}> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{
    items: PublicDiscoverPost[];
    page: number;
    pageSize: number;
    total: number;
  }>(`/api/v1/consumer/discover-posts${suffix}`, { signal });
}

export interface PublicReview {
  id: string;
  merchantId: string;
  rating: number;
  comment: string;
  reviewerName: string | null;
  publishedAt: string | null;
  createdAt: string;
}

export function getMerchantReviews(
  merchantId: string,
  query: { page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<{
  items: PublicReview[];
  page: number;
  pageSize: number;
  total: number;
  averageRating: number | null;
}> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{
    items: PublicReview[];
    page: number;
    pageSize: number;
    total: number;
    averageRating: number | null;
  }>(`/api/v1/consumer/merchants/${encodeURIComponent(merchantId)}/reviews${suffix}`, {
    signal,
  });
}

export interface MyReview {
  id: string;
  merchantId: string;
  rating: number;
  comment: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  moderationNote: string | null;
  orderId: string;
  publishedAt: string | null;
  createdAt: string;
}

export function getMyReviewForOrder(
  orderId: string,
  token: string,
  signal?: AbortSignal,
): Promise<{ review: MyReview | null }> {
  return apiFetch<{ review: MyReview | null }>(
    `/api/v1/consumer/orders/${encodeURIComponent(orderId)}/review`,
    { token, signal },
  );
}

export function submitReview(
  orderId: string,
  payload: { rating: number; comment: string },
  token: string,
  signal?: AbortSignal,
): Promise<{ review: MyReview }> {
  return apiFetch<{ review: MyReview }>(
    `/api/v1/consumer/orders/${encodeURIComponent(orderId)}/review`,
    { method: 'POST', body: payload, token, signal },
  );
}

export interface ConsumerMerchantLite {
  id: string;
  nameZh: string;
  nameKm?: string | null;
  nameEn?: string | null;
  coverImageUrl?: string | null;
  cityCode?: string;
  categoryCode?: string;
}

export interface GrouponDeal {
  id: string;
  merchantId: string;
  merchant: ConsumerMerchantLite;
  titleZh: string;
  titleKm: string;
  titleEn: string;
  descriptionI18n?: { zh?: string; km?: string; en?: string } | null;
  usageRulesI18n?: { zh?: string; km?: string; en?: string } | null;
  refundRulesI18n?: { zh?: string; km?: string; en?: string } | null;
  coverImageUrl?: string | null;
  images?: unknown;
  priceCents: string;
  originalPriceCents: string;
  currency: string;
  status: string;
  saleStartAt?: string | null;
  saleEndAt?: string | null;
  usageStartAt?: string | null;
  usageEndAt?: string | null;
  perUserLimit?: number | null;
  soldCount?: number;
  rules?: string | null;
}

export interface PaginatedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface GrouponListQuery {
  cityCode?: string;
  categoryCode?: string;
  merchantId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

export function getGroupons(
  query: GrouponListQuery = {},
  signal?: AbortSignal,
): Promise<PaginatedResult<GrouponDeal>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<PaginatedResult<GrouponDeal>>(`/api/v1/consumer/groupons${suffix}`, { signal });
}

export function getGrouponById(id: string, signal?: AbortSignal): Promise<GrouponDeal> {
  return apiFetch<GrouponDeal>(`/api/v1/consumer/groupons/${encodeURIComponent(id)}`, { signal });
}

export type OrderStatus =
  | 'CREATED'
  | 'PENDING'
  | 'SUCCEEDED'
  | 'VOUCHERED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'REFUNDED';

export interface ConsumerOrder {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  currency: string;
  totalAmountCents: string;
  unitPriceCents?: string;
  quantity?: number;
  dealId: string;
  expiredAt?: string | null;
  paidAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  voucher?: {
    code: string;
    qrPayload: string;
    status: 'VALID' | 'REDEEMED' | 'EXPIRED' | 'REFUNDED' | string;
    validUntil: string;
    redeemedAt: string | null;
  } | null;
  deal?: Pick<GrouponDeal, 'id' | 'titleZh' | 'titleKm' | 'titleEn' | 'coverImageUrl'> & {
    merchant?: ConsumerMerchantLite;
  };
}

export interface ChannelInfo {
  channelCode: PaymentChannelCode;
  displayName: string;
  icon: string | null;
  enabled: boolean;
}

export function getPaymentChannels(
  token: string,
  signal?: AbortSignal,
): Promise<{ channels: ChannelInfo[] }> {
  return apiFetch<{ channels: ChannelInfo[] }>('/api/v1/consumer/payments/channels', {
    token,
    signal,
  });
}

export interface CreateOrderInput {
  dealId: string;
  quantity: number;
  channelCode?: string;
}

export function createOrder(
  input: CreateOrderInput,
  token: string,
  signal?: AbortSignal,
): Promise<{ order: ConsumerOrder }> {
  return apiFetch<{ order: ConsumerOrder }>('/api/v1/consumer/orders', {
    method: 'POST',
    body: input,
    token,
    signal,
  });
}

export function getOrderById(
  id: string,
  token: string,
  signal?: AbortSignal,
): Promise<{ order: ConsumerOrder }> {
  return apiFetch<{ order: ConsumerOrder }>(
    `/api/v1/consumer/orders/${encodeURIComponent(id)}`,
    { token, signal },
  );
}

export type PaymentChannelCode = 'usdt' | 'aba' | 'wing' | 'alipay_hk' | 'mock';

export interface PaymentIntent {
  id: string;
  channelCode: PaymentChannelCode;
  status: string;
  redirectUrl?: string | null;
  qrCode?: string | null;
  paidCurrency?: string;
  paidAmountCents?: string;
  fxRateSnapshot?: string | null;
  nonce?: string;
  expiresAt?: string | null;
}

export function payOrder(
  orderId: string,
  channel: PaymentChannelCode,
  token: string,
  signal?: AbortSignal,
): Promise<{ intent: PaymentIntent }> {
  return apiFetch<{ intent: PaymentIntent }>(
    `/api/v1/consumer/orders/${encodeURIComponent(orderId)}/pay`,
    {
      method: 'POST',
      body: { channel },
      token,
      signal,
    },
  );
}

export function verifyPaymentReturn(
  input: { nonce: string; orderId: string },
  token: string,
  signal?: AbortSignal,
): Promise<{ order: Pick<ConsumerOrder, 'id' | 'status'> }> {
  return apiFetch<{ order: Pick<ConsumerOrder, 'id' | 'status'> }>(
    '/api/v1/consumer/payments/verify-return',
    {
      method: 'POST',
      body: input,
      token,
      signal,
    },
  );
}

export interface MyOrdersQuery {
  page?: number;
  pageSize?: number;
  status?: OrderStatus | string;
}

/* ----- GAP-03: 收藏系统 ----- */

export interface FavoriteDto {
  id: string;
  targetType: string;
  targetId: string;
  createdAt: string;
}

export interface FavoriteWithTargetDto extends FavoriteDto {
  target?: {
    id: string;
    nameZh: string | null;
    nameKm: string | null;
    nameEn: string | null;
    coverImageUrl: string | null;
  } | null;
}

export function createFavorite(
  payload: { targetType: 'MERCHANT' | 'DISCOVER_POST'; targetId: string },
  token: string,
  signal?: AbortSignal,
): Promise<{ favorite: FavoriteDto }> {
  return apiFetch<{ favorite: FavoriteDto }>('/api/v1/consumer/me/favorites', {
    method: 'POST',
    body: payload,
    token,
    signal,
  });
}

export function deleteFavorite(
  favoriteId: string,
  token: string,
  signal?: AbortSignal,
): Promise<void> {
  return apiFetch<void>(`/api/v1/consumer/me/favorites/${encodeURIComponent(favoriteId)}`, {
    method: 'DELETE',
    token,
    signal,
  });
}

export function listFavorites(
  params: { type?: 'MERCHANT' | 'DISCOVER_POST'; page?: number; pageSize?: number },
  token: string,
  signal?: AbortSignal,
): Promise<{
  items: FavoriteWithTargetDto[];
  page: number;
  pageSize: number;
  total: number;
}> {
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{
    items: FavoriteWithTargetDto[];
    page: number;
    pageSize: number;
    total: number;
  }>(`/api/v1/consumer/me/favorites${suffix}`, { token, signal });
}

/* ----- GAP-04: 订单状态页（nonce-based，无需 JWT） ----- */

export interface OrderStatusByNonceResult {
  status: OrderStatus;
  totalCents: string;
  currency: string;
  dealTitle: string;
  voucherCodes: string[];
  paymentChannel: string | null;
}

/**
 * 通过 nonce 查询订单状态（无需 JWT，用于支付回跳场景）。
 *   GET /api/v1/consumer/orders/status?orderId=xxx&nonce=yyy
 */
export function getOrderStatusByNonce(
  orderId: string,
  nonce: string,
  signal?: AbortSignal,
): Promise<{ order: OrderStatusByNonceResult }> {
  const qs = new URLSearchParams({ orderId, nonce });
  return apiFetch<{ order: OrderStatusByNonceResult }>(
    `/api/v1/consumer/orders/status?${qs.toString()}`,
    { signal },
  );
}

export function getMyOrders(
  query: MyOrdersQuery,
  token: string,
  signal?: AbortSignal,
): Promise<PaginatedResult<ConsumerOrder>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<PaginatedResult<ConsumerOrder>>(`/api/v1/consumer/me/orders${suffix}`, {
    token,
    signal,
  });
}

/* -----------------------------------------------------------------------
 * Consumer CS（SPEC-010 CS-10）
 * --------------------------------------------------------------------- */

export interface ConsumerCsTicket {
  id: string;
  conversationId: string;
  kind: string;
  status: string;
  subject: string;
  updatedAt: string;
}

export interface ConsumerCsTicketsPage {
  items: ConsumerCsTicket[];
  page: number;
  pageSize: number;
  total: number;
}

export interface OpenConsumerTicketInput {
  kind: 'COMPLAINT' | 'REFUND_REQUEST' | 'INQUIRY';
  subject: string;
  body: string;
  orderId?: string;
  merchantId?: string;
}

export interface OpenConsumerTicketResult {
  ticket: ConsumerCsTicket;
  conversation: { id: string; kind: string; status: string };
  csBotDeeplink: string;
}

export function listConsumerCsTickets(
  token: string,
  page = 1,
  signal?: AbortSignal,
): Promise<ConsumerCsTicketsPage> {
  const qs = new URLSearchParams({ page: String(page) });
  return apiFetch<ConsumerCsTicketsPage>(`/api/v1/consumer/cs/tickets/me?${qs.toString()}`, {
    token,
    signal,
  });
}

export function openConsumerCsTicket(
  body: OpenConsumerTicketInput,
  token: string,
  signal?: AbortSignal,
): Promise<OpenConsumerTicketResult> {
  return apiFetch<OpenConsumerTicketResult>('/api/v1/consumer/cs/tickets', {
    method: 'POST',
    token,
    body,
    signal,
  });
}

export interface ConsumerCsMessage {
  id: string;
  conversationId: string;
  senderRole: string;
  senderRefId: string;
  body: string;
  attachmentUrls: string[];
  telegramUpdateId: string | null;
  redactedAt: string | null;
  createdAt: string;
}

export interface ConsumerCsPollMessagesResult {
  messages: ConsumerCsMessage[];
  nextSince: number;
  hasMore: boolean;
}

export function getConsumerCsMessages(
  token: string,
  conversationId: string,
  since: number,
  signal?: AbortSignal,
): Promise<ConsumerCsPollMessagesResult> {
  const qs = new URLSearchParams({ since: String(since) });
  return apiFetch<ConsumerCsPollMessagesResult>(
    `/api/v1/consumer/cs/conversations/${encodeURIComponent(conversationId)}/messages?${qs}`,
    { token, signal },
  );
}

export function sendConsumerCsMessage(
  token: string,
  conversationId: string,
  body: string,
  signal?: AbortSignal,
): Promise<{ message: ConsumerCsMessage }> {
  return apiFetch<{ message: ConsumerCsMessage }>(
    `/api/v1/consumer/cs/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: 'POST',
      token,
      body: { body },
      signal,
    },
  );
}

export function submitConsumerCsCsat(
  token: string,
  ticketId: string,
  body: { rating: number; comment?: string },
  signal?: AbortSignal,
): Promise<{ ticket: ConsumerCsTicket }> {
  return apiFetch<{ ticket: ConsumerCsTicket }>(
    `/api/v1/consumer/cs/tickets/${encodeURIComponent(ticketId)}/csat`,
    {
      method: 'POST',
      token,
      body,
      signal,
    },
  );
}

export function trackConsumerBehavior(
  body: { eventName: string; payload?: Record<string, unknown> },
  token: string,
  signal?: AbortSignal,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/v1/consumer/behavior/track', {
    method: 'POST',
    token,
    body,
    signal,
  });
}
